import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown, ChevronsLeft, ChevronsRight, Zap } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { useSubscription } from "@/hooks/queries/useDashboardData";
import { PromoCard } from "@/components/widgets";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { isPathActive, primaryNav, secondaryNav, visibleNav, type NavItem } from "./navigation";

interface SidebarProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  /** Render in-flow (inside the mobile sheet) instead of fixed to the viewport. */
  embedded?: boolean;
}

const PROMO_DISMISS_KEY = "sidebar_promo_dismissed";

export function BrandMark({ compact = false, to = "/dashboard" }: { compact?: boolean; to?: string }) {
  return (
    <Link to={to} className="flex items-center gap-2 select-none" aria-label="LLM Gateway home">
      <span className="h-8 w-8 rounded-[10px] bg-primary text-primary-foreground flex items-center justify-center flex-shrink-0">
        <span className="h-3 w-3 rounded-[3px] bg-brand" />
      </span>
      {!compact && (
        <span className="text-[19px] font-semibold tracking-tight leading-none">
          LLM<span className="text-brand-strong">.</span>
          <span className="text-muted-foreground font-medium text-[15px] ml-1">Gateway</span>
        </span>
      )}
    </Link>
  );
}

function NavRow({ item, open, pathname }: { item: NavItem; open: boolean; pathname: string }) {
  const childActive = item.children?.some((c) => isPathActive(pathname, c.href)) ?? false;
  const selfActive = item.href ? isPathActive(pathname, item.href) : false;
  const [expanded, setExpanded] = useState(childActive);

  useEffect(() => {
    if (childActive) setExpanded(true);
  }, [childActive]);

  const rowClass = cn(
    "relative flex w-full items-center gap-3 rounded-xl px-3 h-11 text-[15px] transition-colors",
    selfActive || (childActive && !open) ? "bg-surface-muted text-foreground" : "text-foreground/80 hover:bg-surface-muted/70 hover:text-foreground"
  );
  const indicator = (selfActive || (childActive && !open)) && (
    <span className="absolute -left-3 top-1/2 -translate-y-1/2 h-6 w-[3px] rounded-r-full bg-primary" />
  );
  const icon = <item.icon className="h-[19px] w-[19px] flex-shrink-0" strokeWidth={1.6} />;

  if (!open) {
    const target = item.href ?? item.children![0].href;
    return (
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>
          <Link to={target} className={cn(rowClass, "justify-center px-0")} aria-label={item.name}>
            {indicator}
            {icon}
          </Link>
        </TooltipTrigger>
        <TooltipContent side="right" className="rounded-lg">
          {item.name}
        </TooltipContent>
      </Tooltip>
    );
  }

  if (item.href) {
    return (
      <Link to={item.href} className={rowClass} aria-current={selfActive ? "page" : undefined}>
        {indicator}
        {icon}
        <span className="truncate">{item.name}</span>
      </Link>
    );
  }

  return (
    <div>
      <button type="button" onClick={() => setExpanded((e) => !e)} className={rowClass} aria-expanded={expanded}>
        {icon}
        <span className="truncate flex-1 text-left">{item.name}</span>
        <ChevronDown className={cn("h-4 w-4 text-muted-foreground transition-transform", expanded && "rotate-180")} />
      </button>
      {expanded && (
        <ul className="ml-[21px] mt-1 mb-1 border-l border-border pl-4 flex flex-col gap-0.5">
          {item.children!.map((c) => {
            const active = isPathActive(pathname, c.href);
            return (
              <li key={c.href} className="relative">
                <span className="absolute -left-4 top-1/2 h-px w-3 bg-border" />
                <Link
                  to={c.href}
                  aria-current={active ? "page" : undefined}
                  className={cn(
                    "flex items-center h-9 px-2 rounded-lg text-sm transition-colors",
                    active ? "bg-surface-muted text-foreground font-medium" : "text-foreground/70 hover:text-foreground"
                  )}
                >
                  {c.name}
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export const Sidebar = ({ open, setOpen, embedded = false }: SidebarProps) => {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const { data: subscription } = useSubscription();
  const [promoDismissed, setPromoDismissed] = useState(() => localStorage.getItem(PROMO_DISMISS_KEY) === "1");

  const tier = (subscription?.tier || "").toLowerCase();
  const showPromo = open && !promoDismissed && (!subscription || tier === "free" || tier === "starter");

  return (
    <aside
      className={cn(
        "bg-sidebar text-sidebar-foreground flex flex-col transition-[width] duration-300",
        embedded ? "h-full w-full" : "fixed inset-y-0 left-0 z-[100]",
        !embedded && (open ? "w-[264px]" : "w-[84px]")
      )}
    >
      <div className={cn("h-[72px] flex items-center flex-shrink-0", open ? "px-6" : "justify-center")}>
        <BrandMark compact={!open} />
      </div>

      <nav className={cn("flex-1 overflow-y-auto overflow-x-hidden pb-4", open ? "px-4" : "px-3")} aria-label="Main">
        <div className="flex flex-col gap-1">
          {visibleNav(primaryNav, isAdmin).map((item) => (
            <NavRow key={item.name} item={item} open={open} pathname={pathname} />
          ))}
        </div>
        <div className="my-4 mx-3 h-px bg-border" />
        <div className="flex flex-col gap-1">
          {secondaryNav.map((item) => (
            <NavRow key={item.name} item={item} open={open} pathname={pathname} />
          ))}
        </div>
      </nav>

      <div className={cn("flex-shrink-0 pb-4", open ? "px-4" : "px-3")}>
        {showPromo && (
          <div className="mb-3">
            <PromoCard
              icon={<Zap className="h-4 w-4 fill-current" />}
              title="Upgrade to Pro"
              body="Higher limits, intelligent routing and full cost analytics."
              cta="Upgrade now"
              to="/billing"
              onDismiss={() => {
                localStorage.setItem(PROMO_DISMISS_KEY, "1");
                setPromoDismissed(true);
              }}
            />
          </div>
        )}
        {!embedded && <button
          type="button"
          onClick={() => setOpen(!open)}
          className={cn(
            "flex w-full items-center gap-3 h-11 rounded-xl text-[15px] text-foreground/80 hover:bg-surface-muted/70 hover:text-foreground transition-colors",
            open ? "px-3" : "justify-center"
          )}
          aria-label={open ? "Collapse sidebar" : "Expand sidebar"}
        >
          {open ? <ChevronsLeft className="h-[19px] w-[19px]" strokeWidth={1.6} /> : <ChevronsRight className="h-[19px] w-[19px]" strokeWidth={1.6} />}
          {open && <span>Collapse sidebar</span>}
        </button>}
      </div>
    </aside>
  );
};
